import { computed, ref, toValue, watch } from 'vue'
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue'
import Network from '@/Network'
import { ServiceError, getApiErrorMessage } from '@/utils/apiError'
import type { CoverageResponsibleResponse } from '@/Network/Types/Responses/CoverageResponses'
import type { UpdateCoverageResponsibleRequest } from '@/Network/Types/Requests/CoverageRequests'

export interface UseCoverageResponsibles {
  items: Ref<CoverageResponsibleResponse[]>
  selectableItems: ComputedRef<CoverageResponsibleResponse[]>
  isLoading: Ref<boolean>
  loadError: Ref<string | null>
  isSupported: Ref<boolean>
  reload: () => Promise<void>
  create: (name: string) => Promise<CoverageResponsibleResponse>
  update: (
    responsibleId: number,
    payload: UpdateCoverageResponsibleRequest
  ) => Promise<CoverageResponsibleResponse>
}

const byName = (a: CoverageResponsibleResponse, b: CoverageResponsibleResponse) =>
  a.name.localeCompare(b.name, 'pt-BR')

/**
 * Lista de "Responsável pela cobertura" de UMA unidade (Q28). Sem store global:
 * a lista é pequena, muda pouco e cada tela que a usa recarrega ao montar.
 */
export function useCoverageResponsibles(
  healthUnitId: MaybeRefOrGetter<number | null | undefined>,
  options: { includeInactive?: boolean } = {}
): UseCoverageResponsibles {
  const items = ref<CoverageResponsibleResponse[]>([])
  const isLoading = ref(false)
  const loadError = ref<string | null>(null)
  const isSupported = ref(true)
  let requestSeq = 0

  const selectableItems = computed(() =>
    items.value.filter((item) => item.isActive && !item.isSystem)
  )

  async function reload(): Promise<void> {
    const unitId = toValue(healthUnitId)
    const seq = ++requestSeq
    if (!unitId) {
      items.value = []
      loadError.value = null
      isLoading.value = false
      return
    }
    isLoading.value = true
    loadError.value = null
    try {
      const result = await Network.coverageResponsible.list(unitId, {
        includeInactive: options.includeInactive ?? false
      })
      if (seq !== requestSeq) return
      if (result === null) {
        isSupported.value = false
        items.value = []
        return
      }
      isSupported.value = true
      items.value = [...result].sort(byName)
    } catch (e) {
      if (seq !== requestSeq) return
      items.value = []
      loadError.value = getApiErrorMessage(
        e,
        'Não foi possível carregar os responsáveis pela cobertura'
      )
    } finally {
      if (seq === requestSeq) isLoading.value = false
    }
  }

  async function create(name: string): Promise<CoverageResponsibleResponse> {
    const unitId = toValue(healthUnitId)
    if (!unitId) throw new ServiceError('Selecione a unidade antes de criar um responsável.')
    const created = await Network.coverageResponsible.create(unitId, { name: name.trim() })
    items.value = [...items.value, created].sort(byName)
    return created
  }

  async function update(
    responsibleId: number,
    payload: UpdateCoverageResponsibleRequest
  ): Promise<CoverageResponsibleResponse> {
    const unitId = toValue(healthUnitId)
    if (!unitId) throw new ServiceError('Selecione a unidade antes de editar um responsável.')
    const updated = await Network.coverageResponsible.update(unitId, responsibleId, payload)
    items.value = items.value.map((item) => (item.id === updated.id ? updated : item)).sort(byName)
    return updated
  }

  watch(
    () => toValue(healthUnitId),
    () => {
      void reload()
    },
    { immediate: true }
  )

  return { items, selectableItems, isLoading, loadError, isSupported, reload, create, update }
}
